import { useState } from "react";
import { Download, Eye, EyeOff, Star, Loader2, AlertCircle, Users, Mail, MailCheck, Square, FileX2 } from "lucide-react";
import { AccessLog } from "@/components/apps/AccessLog";
import { DeleteFileButton } from "@/components/apps/DeleteFileButton";
import { TypeBadge } from "@/components/apps/TypeBadge";
import { Checksum } from "@/components/apps/Checksum";
import { fmtSize, fmtDate } from "@/lib/appsApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/apps`;
const COLS = "md:grid md:grid-cols-[120px_110px_110px_1fr_auto] md:items-center md:gap-6";

const fmtDay = (d) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(d || "");
  if (!m) return "—";
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

const Cell = ({ label, children, testid }) => (
  <div className="flex gap-3 md:block" data-testid={testid}>
    <span className="w-20 shrink-0 font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500 md:hidden">{label}</span>
    <div className="min-w-0 text-sm text-ink">{children}</div>
  </div>
);

const NotifyButton = ({ f, onNotify }) => {
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (f.notified_at) {
    return (
      <span title={`Testers emailed ${fmtDate(f.notified_at)}${f.notified_by ? ` by ${f.notified_by}` : ""}`} className="rounded-full bg-emerald-50 p-2 text-emerald-700" data-testid={`apps-file-notified-${f.id}`}>
        <MailCheck className="h-4 w-4" />
      </span>
    );
  }
  const click = async () => {
    if (!confirm) return setConfirm(true);
    setBusy(true);
    await onNotify(f.id);
    setBusy(false);
    setConfirm(false);
    return undefined;
  };
  return (
    <button onClick={click} onBlur={() => !busy && setConfirm(false)} disabled={busy} title="Email testers about this file (once only)"
      className={`inline-flex items-center gap-1 rounded-full px-2 py-2 text-xs font-medium transition-colors ${confirm ? "bg-ink px-3 text-white" : "text-slate-500 hover:bg-sky-50 hover:text-sky-700"}`} data-testid={`apps-file-notify-${f.id}`}>
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}{confirm && (busy ? "Sending" : "Email testers?")}
    </button>
  );
};

const SaveProgress = ({ f, isAdmin, onStop }) => {
  const pct = Math.max(0, Math.min(100, Number(f.save_progress) || 0));
  const [stopping, setStopping] = useState(false);
  const stop = async () => {
    if (!window.confirm(`Stop saving "${f.package_name || f.original_filename}"? The upload will be thrown away and you'll need to upload it again.`)) return;
    setStopping(true);
    await onStop(f.id, f.package_name || f.original_filename);
  };
  return (
    <span className="mt-2 block max-w-sm" data-testid={`apps-file-processing-${f.id}`}>
      <span className="flex items-center justify-between gap-2 text-xs font-medium text-sky-700">
        <span className="flex items-center gap-1.5"><Loader2 className="h-3.5 w-3.5 animate-spin" />{stopping ? "Stopping" : f.save_stage || "Preparing file"}…</span>
        <span className="flex items-center gap-2">
          <span className="font-mono" data-testid={`apps-file-save-pct-${f.id}`}>{pct}%</span>
          {isAdmin && (
            <button type="button" onClick={stop} disabled={stopping} className="inline-flex items-center gap-1 rounded-full border border-red-200 px-2 py-0.5 text-[11px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50" data-testid={`apps-file-stop-${f.id}`}>
              <Square className="h-3 w-3 fill-red-600" />Stop
            </button>
          )}
        </span>
      </span>
      <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-sky-100" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Saving to storage">
        <span className="block h-full rounded-full bg-sky-600 transition-[width] duration-700 ease-out" style={{ width: `${pct}%` }} />
      </span>
    </span>
  );
};

const MoveSelect = ({ f, tabs, onMove }) => (
  <label className="mt-2 flex items-center gap-2 text-xs text-slate-600">
    <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">Tab</span>
    <select value={f.tab_id || ""} onChange={(e) => onMove(f.id, e.target.value)} className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs text-ink focus:border-sky-600 focus:outline-none" data-testid={`apps-file-move-${f.id}`}>
      {tabs.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
    </select>
  </label>
);

const FileRow = ({ f, tabs, typeOrder, isAdmin, onFlag, onNotify, onDelete, onMove, onStop, latestToggle }) => {
  const [log, setLog] = useState(false);
  const ready = f.status === "ready" && !f.missing;
  return (
    <article className={`space-y-2 rounded-3xl border p-5 md:space-y-0 md:rounded-2xl md:px-6 md:py-4 ${COLS} ${f.hidden ? "border-dashed border-slate-300 bg-slate-50 opacity-70" : f.latest ? "border-sky-400 bg-white shadow-[0_10px_30px_-20px_rgba(2,132,199,0.6)]" : "border-sky-100 bg-white"}`} data-testid={`apps-file-${f.id}`}>
      <Cell label="Date" testid={`apps-file-date-${f.id}`}>
        {fmtDay(f.file_date || (f.created_at || "").slice(0, 10))}
        {f.latest && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-sky-600 px-2 py-0.5 align-middle text-[11px] font-semibold text-white md:ml-0 md:mt-1 md:flex md:w-fit" data-testid={`apps-file-latest-badge-${f.id}`}><Star className="h-3 w-3 fill-white" />Latest</span>}
        {f.hidden && <span className="ml-2 inline-flex rounded-full bg-slate-200 px-2 py-0.5 align-middle text-[11px] font-semibold text-slate-700 md:ml-0 md:mt-1 md:flex md:w-fit" data-testid={`apps-file-hidden-badge-${f.id}`}>Hidden</span>}
      </Cell>
      <Cell label="Version" testid={`apps-file-version-${f.id}`}>
        <span className="font-mono">{f.version || "—"}</span>
        {f.version_code != null && <span className="mt-0.5 block font-mono text-[11px] text-slate-500" data-testid={`apps-file-version-code-${f.id}`}>{f.file_type === "Mission Ready Bundle" ? "build" : "code"} {f.version_code}</span>}
        {(f.min_sdk || f.target_sdk) && <span className="block font-mono text-[11px] text-slate-500" data-testid={`apps-file-sdk-${f.id}`}>SDK {f.min_sdk || "?"}–{f.target_sdk || "?"}</span>}
      </Cell>
      <Cell label="Type" testid={`apps-file-type-${f.id}`}>
        <TypeBadge type={f.file_type} order={typeOrder} testid={`apps-file-type-badge-${f.id}`} />
      </Cell>
      <Cell label="File" testid={`apps-file-desc-${f.id}`}>
        <span className="block break-words font-semibold text-ink" data-testid={`apps-file-name-${f.id}`}>{f.package_name || f.original_filename}</span>
        {f.package_name && <span className="block break-all font-mono text-[11px] text-slate-500" data-testid={`apps-file-filename-${f.id}`}>{f.original_filename}</span>}
        <span className="mt-1 block whitespace-pre-line leading-relaxed text-slate-700">{f.description}</span>
        {ready && <Checksum f={f} isAdmin={isAdmin} />}
        {f.status === "processing" && <SaveProgress f={f} isAdmin={isAdmin} onStop={onStop} />}
        {f.missing && <span className="mt-1 flex items-start gap-1.5 text-xs font-medium text-red-700" data-testid={`apps-file-missing-${f.id}`}><FileX2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />Not found in storage, so it can't be downloaded. Remove this entry with the bin icon and upload the file again.</span>}
        {f.status === "failed" && <span className="mt-1 flex items-start gap-1.5 text-xs font-medium text-red-700" data-testid={`apps-file-failed-${f.id}`}><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />{f.error || "Saving failed."} Upload the file again, and remove this entry with the bin icon.</span>}
        {isAdmin && (
          <button onClick={() => setLog(!log)} aria-pressed={log} className="mt-1 flex items-center gap-1 text-xs font-medium text-sky-700 hover:text-sky-900" data-testid={`apps-file-downloads-${f.id}`}>
            <Users className="h-3.5 w-3.5" />{f.download_count ?? 0} download{f.download_count === 1 ? "" : "s"} · {log ? "hide" : "who & when"}
          </button>
        )}
        {isAdmin && log && <AccessLog params={{ file_id: f.id }} show="user" testid={`apps-file-log-${f.id}`} />}
        {isAdmin && tabs.length > 1 && <MoveSelect f={f} tabs={tabs} onMove={onMove} />}
        {isAdmin && f.notified_at && <span className="mt-1 block text-xs text-emerald-700" data-testid={`apps-file-notified-text-${f.id}`}>Testers emailed {fmtDate(f.notified_at)}{f.notified_count ? ` · ${f.notified_count} sent` : ""}</span>}
      </Cell>
      <div className="flex items-center gap-2 pt-2 md:justify-end md:pt-0">
        {ready ? (
          <a href={`${API}/files/${f.id}/download`} title={fmtSize(f.size)} className="inline-flex items-center gap-2 rounded-full bg-sky-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-sky-700" data-testid={`apps-file-link-${f.id}`}>
            <Download className="h-4 w-4" /> get
          </a>
        ) : (
          <span className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-4 py-2 text-sm font-medium text-slate-500" data-testid={`apps-file-link-${f.id}`}><Download className="h-4 w-4" /> get</span>
        )}
        {isAdmin && (
          <>
            <NotifyButton f={f} onNotify={onNotify} />
            {latestToggle && <button onClick={() => onFlag(f.id, { latest: !f.latest })} aria-pressed={f.latest} title={f.latest ? "Remove Latest badge" : "Mark as Latest"}
              className={`rounded-full p-2 transition-colors ${f.latest ? "bg-sky-100 text-sky-700" : "text-slate-500 hover:bg-sky-50 hover:text-sky-700"}`} data-testid={`apps-file-latest-toggle-${f.id}`}>
              <Star className={`h-4 w-4 ${f.latest ? "fill-sky-600" : ""}`} />
            </button>}
            <button onClick={() => onFlag(f.id, { hidden: !f.hidden })} aria-pressed={f.hidden} title={f.hidden ? "Show to testers" : "Hide from testers"}
              className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-ink" data-testid={`apps-file-hide-toggle-${f.id}`}>
              {f.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <DeleteFileButton f={f} onDelete={onDelete} />
          </>
        )}
      </div>
    </article>
  );
};

export const FileList = ({ files, tabs = [], typeOrder = [], isAdmin, onFlag, onNotify, onDelete, onMove, onStop, latestToggle = true, emptyText = "No files in this tab yet." }) => (
  <div className="space-y-3" data-testid="apps-file-list">
    {files.length > 0 && (
      <div className={`hidden px-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 ${COLS}`}>
        <span>Date</span><span>Version</span><span>Type</span><span>File &amp; description</span><span className={isAdmin ? "w-[230px]" : "w-[72px]"} />
      </div>
    )}
    {files.length === 0 && <p className="rounded-3xl border border-dashed border-sky-200 bg-white p-10 text-center text-slate-600" data-testid="apps-files-empty">{emptyText}</p>}
    {files.map((f) => <FileRow key={f.id} f={f} tabs={tabs} typeOrder={typeOrder} isAdmin={isAdmin} onFlag={onFlag} onNotify={onNotify} onDelete={onDelete} onMove={onMove} onStop={onStop} latestToggle={latestToggle} />)}
  </div>
);
